import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/shared/ui/card';
import { Edit, Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/ui/avatar';
import { useCollections, useDeleteCollection } from '../model';
import { GetCollectionsParams, CollectionListItem, CollectionRedirectType } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Pagination, DataTable } from '@/shared/widgets';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { useTranslation } from '@/shared/i18n';

export const CollectionsList = () => {
  const { t } = useTranslation('collections');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page') ?? '1') || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize') ?? '10') || 10);
  const [isActive, setIsActive] = useState<boolean | undefined>(
    searchParams.get('isActive') === 'true' ? true : searchParams.get('isActive') === 'false' ? false : undefined
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<CollectionListItem | null>(null);

  const filtrosApi: GetCollectionsParams = {
    page: currentPage,
    limit: pageSize,
    isActive,
  };

  const {
    collections,
    totalPages,
    totalItems,
    currentPage: currentPageFromApi,
    isLoading,
    error,
    queryResult,
  } = useCollections(filtrosApi);

  const { deleteCollection, isLoading: isDeleting } = useDeleteCollection();

  // Sincronizar URL com estado local
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 10) params.set('pageSize', String(pageSize));
    if (isActive !== undefined) params.set('isActive', String(isActive));
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, isActive, setSearchParams]);

  // Resetar página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [isActive, pageSize]);

  const handleEdit = (id: string) => navigate(`/mobile/collections/${id}`);
  const handleDelete = (collection: CollectionListItem) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!collectionToDelete) return;
    try {
      await deleteCollection(collectionToDelete.id);
      setDeleteDialogOpen(false);
      setCollectionToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir coleção:', error);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString || dateString === '') return '-';
    try {
      // Se for apenas data (YYYY-MM-DD), adiciona hora para parsing
      const date = dateString.match(/^\d{4}-\d{2}-\d{2}$/) 
        ? new Date(`${dateString}T00:00:00.000Z`)
        : new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const getRedirectTypeLabel = (type?: CollectionRedirectType) => {
    switch (type) {
      case CollectionRedirectType.PACKS:
        return t('packs');
      case CollectionRedirectType.PRESETS:
        return t('presets');
      case CollectionRedirectType.CREATORS:
        return t('creators');
      case CollectionRedirectType.LIST_OF_PACKS:
        return t('listOfPacks');
      case CollectionRedirectType.LIST_OF_PRESETS:
        return t('listOfPresets');
      case CollectionRedirectType.SUBSCRIPTION:
        return t('subscription');
      default:
        return '-';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('collections')}</h1>
          <p className="text-muted-foreground">{t('manageCollections')}</p>
        </div>
        <Button onClick={() => navigate('/mobile/collections/new')}>
          <Plus className="mr-2 h-4 w-4" /> {t('newCollection')}
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>{t('filters')}</CardTitle>
          <CardDescription>{t('filterByStatus')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <select
              value={isActive === undefined ? '' : String(isActive)}
              onChange={(e) => setIsActive(e.target.value === '' ? undefined : e.target.value === 'true')}
              className="px-4 py-2 bg-[#333333] border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[#C1174F]"
            >
              <option value="">{t('all')}</option>
              <option value="true">{t('active')}</option>
              <option value="false">{t('inactive')}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={collections}
        isLoading={isLoading}
        error={error}
        emptyTitle={t('emptyTitle')}
        emptyDescription={(hasFilters) =>
          hasFilters
            ? t('emptyDescriptionWithFilters')
            : t('emptyDescription')
        }
        emptyActionLabel={t('emptyActionLabel')}
        emptyActionOnClick={() => navigate('/mobile/collections/new')}
        hasFilters={isActive !== undefined}
        errorMessage={`${t('errorLoadingCollections')}: ${error?.message}`}
        columns={[
          {
            header: t('image'),
            cell: (item) => (
              <div className="flex items-center">
                {item.cover ? (
                  <Avatar className="h-12 w-12 rounded-md">
                    <AvatarImage src={item.cover} alt={item.name} className="object-cover" />
                    <AvatarFallback className="rounded-md bg-white/10">
                      <ImageIcon className="h-6 w-6 text-white/50" />
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="h-12 w-12 rounded-md bg-white/10 flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-white/50" />
                  </div>
                )}
              </div>
            ),
            className: "w-20",
          },
          {
            header: t('name'),
            cell: (item) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium text-white">{item.name}</span>
                {item.description && (
                  <span className="text-xs text-gray-400 line-clamp-2">{item.description}</span>
                )}
              </div>
            ),
            className: "min-w-[200px]",
          },
          {
            header: t('type'),
            cell: (item) => (
              <Badge 
                variant="outline" 
                className="bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20"
              >
                {getRedirectTypeLabel(item.redirectType)}
              </Badge>
            ),
          },
          {
            header: t('period'),
            cell: (item) => {
              const startDate = formatDate(item.startDate);
              const endDate = formatDate(item.endDate);
              const hasPeriod = startDate !== '-' && endDate !== '-';
              
              return (
                <div className="flex flex-col gap-0.5 text-sm">
                  {hasPeriod ? (
                    <>
                      <span className="text-white">{startDate}</span>
                      <span className="text-gray-400">{t('until')} {endDate}</span>
                    </>
                  ) : (
                    <span className="text-gray-500">{t('noPeriodDefined')}</span>
                  )}
                </div>
              );
            },
            className: "min-w-[140px]",
          },
          {
            header: t('status'),
            cell: (item) => (
              <Badge
                variant={item.isActive ? 'default' : 'secondary'}
                className={item.isActive ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'}
              >
                {item.isActive ? t('active') : t('inactive')}
              </Badge>
            ),
          },
          {
            header: t('createdAt'),
            cell: (item) => (
              <span className="text-sm text-gray-400">
                {formatDateTime(item.createdAt)}
              </span>
            ),
            className: "min-w-[140px]",
          },
          {
            header: t('actions'),
            cell: (item) => (
              <div className="flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleEdit(item.id)} 
                  title={t('edit')}
                  className="hover:bg-white/10"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleDelete(item)} 
                  title={t('delete')}
                  className="hover:bg-destructive/20"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ),
            className: "text-right w-24",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showTotalItems
          syncWithQueryParams={false}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteCollection')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteCollectionMessage', { name: collectionToDelete?.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('deleteCollectionDeleting') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
